// Complete implementation of feedback functionality in ChatbotWidget.js

import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { BsChatDots, BsX } from "react-icons/bs";
import { AiOutlineSend, AiOutlineLike, AiOutlineDislike } from "react-icons/ai";
import { FiCopy, FiUser } from "react-icons/fi";
import { IoIosOptions } from "react-icons/io";
import Dropdown from "components/dropdown";
import Request from "helpers/request";
import { urls } from "helpers/urls";
import CommonNotification from "./common/CommonNotification";
import { Spin } from "antd";

const ChatbotWidget = ({
  // ... all your existing props
  widgetPosition,
  headerColor,
  agentMessageColor,
  agentTextColor,
  widgetIconColor,
  widgetIcon = null,
  input = "",
  setInput = () => {},
  isLoading = false,
  setIsLoading = () => {},
  messagesEndRef = null,
  bot_type = "readonly",
  isOpen = false,
  setIsOpen = () => {},
  liked = false,
  setLiked = () => {},
  disliked = false,
  setDisliked = () => {},
  copiedMsg = false,
  setCopiedMsg = () => {},
  messages = [],
  onSendMessage = () => {},
  onStartTyping = () => {},
  onFinishTyping = () => {},
  sessionId = null,
  siteId = null,
  isSticky = true,
}) => {
  // ... all your existing state and useEffect hooks

  const localMessagesEndRef = useRef(null);
  const endRef = messagesEndRef || localMessagesEndRef;
  const [messageStates, setMessageStates] = useState({});
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [typingMessage, setTypingMessage] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);

  // Utility function to strip HTML tags
  const stripHtmlTags = (str) => {
    if (!str) return "";
    return str.replace(/<[^>]*>/g, "");
  };

  // Updated handleMessageRating function with API integration
  const handleMessageRating = async (messageIndex, action) => {
    const message = messages[messageIndex];
    const currentState = messageStates[messageIndex] || {
      liked: false,
      disliked: false,
      copied: false,
    };

    // Validate required data
    if (!message?.id || !sessionId || !siteId) {
      console.error("Missing required data for feedback:", {
        messageId: message?.id,
        sessionId,
        siteId,
      });
      CommonNotification.error(
        "Unable to record feedback. Missing required information."
      );
      return;
    }

    // Additional validation: Check if message ID looks like a fallback ID
    if (message.id.includes("agent_") && message.id.includes("_0.")) {
      console.warn(
        "Warning: Message ID appears to be a fallback ID:",
        message.id
      );
      CommonNotification.error(
        "Message ID is invalid. Please refresh the page and try again."
      );
      return;
    }

    // Determine the new feedback value based on current state and action
    let feedbackValue = 0; // 0 = no feedback (neutral)

    if (action === "like") {
      // If already liked, remove like (set to 0), otherwise set to like (1)
      feedbackValue = currentState.liked ? 0 : 1;
    } else if (action === "dislike") {
      // If already disliked, remove dislike (set to 0), otherwise set to dislike (-1)
      feedbackValue = currentState.disliked ? 0 : -1;
    }

    // Update local state immediately for better UX
    const newState = {
      ...currentState,
      liked: feedbackValue === 1,
      disliked: feedbackValue === -1,
    };

    setMessageStates((prev) => ({
      ...prev,
      [messageIndex]: newState,
    }));

    // Prepare payload for API
    const payload = {
      session_id: sessionId,
      feedback: feedbackValue,
      site_id: siteId,
      message_id: message.id,
    };

    // Send feedback to API
    try {
      const response = await Request.postJson(urls.chatFeedback, payload);

      if (response?.data?.success) {
        console.log("Feedback recorded successfully:", response.data.message);
        // Optional: Show success notification (uncomment if desired)
        // CommonNotification.success(`Feedback ${feedbackValue === 1 ? 'like' : feedbackValue === -1 ? 'dislike' : 'removed'} recorded`);
      } else {
        throw new Error("API response indicates failure");
      }
    } catch (error) {
      console.error("Failed to send feedback:", error);
      console.error("Error details:", error.response?.data);

      // Revert the local state on error
      setMessageStates((prev) => ({
        ...prev,
        [messageIndex]: currentState,
      }));

      // Show error notification with more details
      const errorMessage =
        error.response?.data?.message ||
        "Failed to record feedback. Please try again.";
      CommonNotification.error(errorMessage);
    }
  };

  // Handle message copy functionality
  const handleCopyMessage = (messageIndex, text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setMessageStates((prev) => ({
          ...prev,
          [messageIndex]: {
            ...prev[messageIndex],
            copied: true,
          },
        }));

        // Reset copy state after 2 seconds
        setTimeout(() => {
          setMessageStates((prev) => ({
            ...prev,
            [messageIndex]: {
              ...prev[messageIndex],
              copied: false,
            },
          }));
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        CommonNotification.error("Failed to copy message");
      });
  };

  // Handle form submission with typing animation
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Call the parent component's message handler with typing animation
    await onSendMessage(input.trim(), animateTyping);
  };

  // Handle Enter key press with typing animation
  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        await onSendMessage(input.trim(), animateTyping);
      }
    }
  };

  // Typing animation effect with sources support and optimized performance
  const animateTyping = (text, messageId, sources = null) => {
    const fullText = stripHtmlTags(text);
    return new Promise((resolve) => {
      // Clear any existing typing animation
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }

      setTypingMessage({
        id: messageId,
        text: "",
        type: "agent",
        isTyping: true,
      });

      let currentIndex = 0;
      const typingSpeed = 25; // Slightly faster for better performance
      const chunkSize = 2; // Type multiple characters at once for smoother animation

      const typeNextChunk = () => {
        if (currentIndex < fullText.length) {
          // Type multiple characters at once for better performance
          const nextChunk = Math.min(chunkSize, fullText.length - currentIndex);

          setTypingMessage((prev) => ({
            ...prev,
            text: fullText.slice(0, currentIndex + nextChunk),
          }));

          currentIndex += nextChunk;

          // Use requestAnimationFrame for smoother animation
          const timeout = setTimeout(() => {
            requestAnimationFrame(typeNextChunk);
          }, typingSpeed);

          setTypingTimeout(timeout);
        } else {
          // Typing animation complete
          setTypingMessage(null);
          resolve();
        }
      };

      // Start animation on next frame
      requestAnimationFrame(typeNextChunk);
    });
  };

  // Auto-scroll to bottom when new messages arrive or typing animation updates (throttled)
  useEffect(() => {
    // Throttle scroll updates during typing animation
    const scrollToBottom = () => {
      if (endRef.current) {
        endRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest", // Less aggressive scrolling
        });
      }
    };

    // Use requestAnimationFrame to avoid blocking scroll events
    const timeoutId = requestAnimationFrame(scrollToBottom);

    return () => cancelAnimationFrame(timeoutId);
  }, [messages, typingMessage?.text?.length, endRef, setMessageStates]); // Only trigger on message changes or text length changes

  // Initialize message states when messages change
  useEffect(() => {
    setMessageStates((prevStates) => {
      const newStates = { ...prevStates };
      let hasChanges = false;

      messages.forEach((message, index) => {
        if (!newStates[index]) {
          // NEW: Check if message has a rating and initialize accordingly
          if (message.type === "agent" && message.rating !== undefined) {
            newStates[index] = {
              liked: message.rating === 1,
              disliked: message.rating === -1,
              copied: false,
            };
          } else {
            newStates[index] = {
              liked: false,
              disliked: false,
              copied: false,
            };
          }
          hasChanges = true;
        }
      });

      return hasChanges ? newStates : prevStates;
    });
  }, [messages, setMessageStates]);

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  // Handle email transcript submission
  const handleEmailTranscriptSubmit = async () => {
    try {
      setIsSendingEmail(true);
      const response = await Request.postJson(urls.emailTranscript, {
        session_id: sessionId,
        email: emailInput,
        site_id: siteId,
      });

      if (response?.data?.success === true) {
        CommonNotification.success(response?.data?.message);
        setShowEmailPopup(false);
        setEmailInput("");
        setIsEmailValid(false);
      } else {
        CommonNotification.error("No messages found in this session");
      }
    } catch (error) {
      console.error("Error sending transcript:", error);
      CommonNotification.error(error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handle closing chat widget
  const handleCloseChatWidget = async () => {
    try {
      const storedSessions = JSON.parse(
        localStorage.getItem("chat_session_id") || "{}"
      );

      // Try to find matching key by value
      const keyToDelete = Object.keys(storedSessions).find(
        (k) => storedSessions[k] === sessionId
      );

      // Remove locally if found
      if (keyToDelete) {
        delete storedSessions[keyToDelete];
        localStorage.setItem("chat_session_id", JSON.stringify(storedSessions));
      }

      // Always attempt to delete on server
      if (sessionId && siteId) {
        await Request.deleteJson(urls.deleteChatSession, {
          session_id: sessionId,
          site_id: siteId,
        });
      }
    } catch (error) {
      console.error("Failed to delete chat session:", error);
    } finally {
      setIsOpen(false);
    }
  };

  // Create chat session
  const createChatSession = async (siteId) => {
    try {
      const res = await Request.postJson(urls.createChatSession, {
        site_id: siteId,
      });

      const sessionId = res?.data?.data?.session_id;
      if (sessionId) {
        // Get existing object from localStorage
        const existingSessions =
          JSON.parse(localStorage.getItem("chat_session_id")) || {};

        // Update or add the new site_id: session_id pair
        existingSessions[siteId] = sessionId;

        // Save back to localStorage
        localStorage.setItem(
          "chat_session_id",
          JSON.stringify(existingSessions)
        );

        // Return the session ID so parent component can update its state
        return sessionId;
      }
    } catch (err) {
      console.error("Failed to create chat session:", err);
      return null;
    }
  };

  // Function to handle widget icon click
  const handleWidgetIconClick = async () => {
    // Only check for sessionId if this is an admin_widget (DataSource component)
    if (bot_type === "admin_widget" && !sessionId) {
      // If no session ID exists, create a new session
      const newSessionId = await createChatSession(siteId);
      if (newSessionId) {
        // Session created successfully, now open the chat
        setIsOpen(true);
      } else {
        // Session creation failed, show error or handle gracefully
        console.error("Failed to create chat session");
        // You might want to show a notification here
        CommonNotification.error(
          "Failed to start chat session. Please try again."
        );
      }
    } else {
      // For other bot types or when session exists, just toggle visibility
      setIsOpen((prev) => !prev);
    }
  };

  // Enhanced renderMessage function with improved feedback UI
  const renderMessage = (message, index) => {
    // Check if the current message is the one being typed
    const isTypingMessage = typingMessage && typingMessage.id === message.id;
    const messageState = messageStates[index] || {
      liked: false,
      disliked: false,
      copied: false,
    };
    const isAgent = message.type === "agent";

    return (
      <div
        key={`${message.id || index}-${message.timestamp}`}
        className={`op_chatbot_body_message mb-2 inline-flex w-full items-end gap-2 ${
          isAgent ? "justify-start" : "justify-end"
        }`}
      >
        {isAgent && (
          <>
            <div
              className="op_chatbot_body_message_icon mt-[20px] flex-shrink-0 rounded-full p-2"
              style={{ background: agentMessageColor }}
            >
              <BsChatDots
                className="h-4 w-4"
                style={{ color: agentTextColor }}
              />
            </div>
            <div className="max-w-[70%]">
              <div
                className={`op_chatbot_body_message_text -mt-[12px] rounded-t-[10px] rounded-br-[10px] rounded-bl-[3px] px-[15px] py-[10px] text-[16px]`}
                style={{
                  background: agentMessageColor,
                  color: agentTextColor,
                }}
              >
                <div
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: message.text }}
                />
                {message.sources && message.sources.length > 0 && (
                  <div className="op_chatbot_sources mt-3 border-t border-white/20 pt-2">
                    <div className="mb-1 text-[16px] opacity-75">Sources:</div>
                    <div className="flex flex-col gap-1">
                      {message.sources.map((source, sourceIndex) => (
                        <a
                          key={sourceIndex}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block break-words text-[14px] opacity-90 transition-all duration-200 hover:underline hover:opacity-100"
                          title={source.title}
                          style={{ color: agentTextColor }}
                        >
                          <span className="mr-1 inline-block w-3 text-center">
                            📄
                          </span>
                          <span className="break-words">{source.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="op_chatbot_body_message_rating mt-2 flex items-center gap-[5px]">
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.liked ? "bg-blue-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleMessageRating(index, "like")}
                  title={
                    messageState.liked ? "Remove like" : "Like this message"
                  }
                  disabled={isLoading}
                >
                  <AiOutlineLike
                    className="h-4 w-4"
                    style={{
                      color: messageState.liked ? "#3b82f6" : "#d1d5db",
                      fill: messageState.liked ? "#3b82f6" : "#d1d5db",
                    }}
                  />
                </button>
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.disliked ? "bg-red-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleMessageRating(index, "dislike")}
                  title={
                    messageState.disliked
                      ? "Remove dislike"
                      : "Dislike this message"
                  }
                  disabled={isLoading}
                >
                  <AiOutlineDislike
                    className="h-4 w-4"
                    style={{
                      color: messageState.disliked ? "#ef4444" : "#d1d5db",
                      fill: messageState.disliked ? "#ef4444" : "#d1d5db",
                    }}
                  />
                </button>
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.copied ? "bg-green-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleCopyMessage(index, message.text)}
                  title={messageState.copied ? "Copied!" : "Copy message"}
                >
                  <FiCopy
                    className="h-4 w-4"
                    style={{
                      color: messageState.copied ? "#10b981" : "#d1d5db",
                    }}
                  />
                </button>
              </div>
            </div>
          </>
        )}
        {!isAgent && (
          <>
            <div className="max-w-[70%]">
              <div
                className={`op_chatbot_body_message_text -mt-[12px] rounded-t-[10px] rounded-bl-[10px] rounded-br-[3px] bg-gray-200 px-[15px] py-[5px] text-[16px]`}
              >
                <div
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: message.text }}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  className={`cursor-pointer rounded p-1 transition-colors hover:opacity-70 ${
                    messageState.copied ? "bg-green-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleCopyMessage(index, message.text)}
                  title={messageState.copied ? "Copied!" : "Copy message"}
                >
                  <FiCopy
                    className="h-4 w-4"
                    style={{
                      color: messageState.copied ? "#10b981" : "#d1d5db",
                    }}
                  />
                </button>
              </div>
            </div>
            <div className="op_chatbot_body_message_icon flex-shrink-0 rounded-full bg-gray-200 p-2">
              <FiUser className="h-4 w-4 text-gray-600" />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={`op_chatbot_widget font-sans ${
        isSticky
          ? `sticky top-[100px] bottom-0 h-[580px]  ${
              widgetPosition === "bottom-left" ? "left-4" : "right-4"
            }`
          : "relative min-h-[750px] w-full"
      } ${bot_type === "readonly" ? "bg-lightPrimary" : ""}`}
    >
      {/* Main Chat Container - Show based on bot_type and sessionId */}
      {(bot_type === "admin_widget" ? sessionId : true) && (
        <div
          className={`op_chatbot_body op-chatbot-scale absolute  shadow ${
            widgetPosition === "bottom-left"
              ? "left-4 origin-bottom-left"
              : "right-4 origin-bottom-right"
          } flex  flex-col overflow-hidden rounded-md bg-white ${
            isSticky ? "h-[600px] w-[620px]" : "mt-4 h-[560px] w-[450px]"
          }${isOpen ? " open" : ""}`}
          style={{ borderColor: headerColor }}
          aria-hidden={!isOpen}
        >
          {/* Chat Header */}
          <div
            className="op_chatbot_header flex items-center gap-2 p-4"
            style={{ background: headerColor }}
          >
            <div className="op_chatbot_header_icon">
              <BsChatDots className="h-6 w-6 text-white" />
            </div>
            <div className="op_chatbot_header_text flex-grow">
              <h4 className="text-lg font-semibold text-white">
                Chat with Smart Roots Bot
              </h4>
              <p className="text-white">Ask anything you want</p>
            </div>
            <div className="op_chatbot_header_settings cursor-pointer">
              <Dropdown
                button={
                  <div className="flex cursor-pointer items-center gap-2 rounded-md dark:!border-white/10">
                    <IoIosOptions className="h-5 w-5 text-white" />
                  </div>
                }
                children={
                  <ul className="flex w-[120px] flex-col gap-2 rounded-[10px] bg-white shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
                    <li
                      className="linear flex cursor-pointer items-center justify-start rounded-md px-2 py-[8px] text-sm text-navy-700 transition duration-200 hover:bg-gray-200 hover:text-navy-700 dark:text-white dark:hover:bg-white/20 dark:hover:text-white dark:active:bg-white/10"
                      onClick={() => setShowEmailPopup(true)}
                    >
                      Email Transcript
                    </li>
                  </ul>
                }
                classNames={"py-2 top-4 right-0 w-max"}
                animation="origin-top-right transition-all duration-300 ease-in-out"
              />
            </div>
            <div
              className="op_chatbot_header_close cursor-pointer"
              onClick={handleCloseChatWidget}
            >
              <BsX className="h-6 w-6 text-white" />
            </div>
          </div>

          {showEmailPopup && (
            <div className="absolute top-28 left-1/2 z-40 w-80 -translate-x-1/2 rounded-lg border bg-white p-4 shadow-lg">
              <h4 className="mb-2 text-lg font-semibold">Email Transcript</h4>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => {
                  const email = e.target.value;
                  setEmailInput(email);
                  setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
                }}
                placeholder="Enter your email"
                className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-brand-500 focus:outline-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300"
                  onClick={() => setShowEmailPopup(false)}
                >
                  Cancel
                </button>
                <button
                  className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleEmailTranscriptSubmit}
                  disabled={!isEmailValid || isSendingEmail}
                >
                  {isSendingEmail ? (
                    <>
                      Sending... <Spin size="small" />
                    </>
                  ) : (
                    "Send"
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Messages Container */}
          <div className="op_chatbot_body_messages mt-[30px] grid flex-grow content-start gap-4 overflow-y-auto scroll-smooth p-4">
            {messages.length === 0 && !typingMessage ? (
              // Default welcome message when no messages exist
              <div className="op_chatbot_body_message op_chatbot_body_message_agent inline-flex items-end justify-items-end gap-2 justify-self-start">
                <div
                  className="op_chatbot_body_message_icon rounded-full p-2 "
                  style={{ background: agentMessageColor }}
                >
                  <BsChatDots
                    className="h-4 w-4"
                    style={{ color: agentTextColor }}
                  />
                </div>
                <div className="max-w-[80%]">
                  <div
                    className="op_chatbot_body_message_text -mt-[12px] rounded-t-[10px] rounded-br-[10px] rounded-bl-[3px] px-[15px] py-[10px] text-[16px]"
                    style={{
                      background: agentMessageColor,
                      color: agentTextColor,
                    }}
                  >
                    <p>
                      Hello, I'm ELLA, the City's virtual assistant! How can I
                      help today? For City information, please type your
                      question.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Render dynamic messages */}
                {messages.map((message, index) =>
                  renderMessage(message, index)
                )}

                {/* Render typing message */}
                {typingMessage && (
                  <div
                    className="op_chatbot_body_message op_chatbot_body_message_agent inline-flex items-end justify-items-end gap-2 justify-self-start"
                    style={{ willChange: "contents" }} // Optimize for animations
                  >
                    <div
                      className="op_chatbot_body_message_icon rounded-full p-2"
                      style={{ background: agentMessageColor }}
                    >
                      <BsChatDots
                        className="h-4 w-4"
                        style={{ color: agentTextColor }}
                      />
                    </div>
                    <div className="max-w-[80%]">
                      <div
                        className="op_chatbot_body_message_text -mt-[12px] rounded-md rounded-t-[10px] rounded-bl-[10px] rounded-br-[3px] px-[15px] py-[5px] text-[16px]"
                        style={{
                          background: agentMessageColor,
                          color: agentTextColor,
                        }}
                      >
                        <p className="whitespace-pre-wrap">
                          <span style={{ willChange: "contents" }}>
                            {typingMessage.text}
                          </span>
                          <span className="animate-pulse opacity-75">|</span>
                        </p>

                        {/* Show sources during typing if available - inside the message bubble */}
                        {typingMessage.sources &&
                          typingMessage.sources.length > 0 && (
                            <div className="op_chatbot_sources mt-3 border-t border-white/20 pt-2">
                              <div className="mb-1 text-[16px] opacity-75">
                                Sources:
                              </div>
                              <div className="flex flex-col gap-1">
                                {typingMessage.sources.map(
                                  (source, sourceIndex) => (
                                    <a
                                      key={sourceIndex}
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block break-words text-xs opacity-90 transition-all duration-200 hover:underline hover:opacity-100"
                                      title={source.title}
                                      style={{ color: agentTextColor }}
                                    >
                                      <span className="mr-1 inline-block w-3 text-center">
                                        📄
                                      </span>
                                      <span className="break-words">
                                        {source.title}
                                      </span>
                                    </a>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Loading indicator */}
            {isLoading && !typingMessage && (
              <div className="op_chatbot_body_message op_chatbot_body_message_agent inline-flex items-end justify-items-end gap-2 justify-self-start">
                <div
                  className="op_chatbot_body_message_icon rounded-full p-2"
                  style={{ background: agentMessageColor }}
                >
                  <BsChatDots
                    className="h-4 w-4"
                    style={{ color: agentTextColor }}
                  />
                </div>
                <div className="max-w-[80%]">
                  <div
                    className="op_chatbot_body_message_text rounded-md px-2 py-1 text-[16px]"
                    style={{
                      background: agentMessageColor,
                      color: agentTextColor,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1 text-gray-500">
                        {/* <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div> */}
                        {/* <div
                          className="w-2 h-2 bg-current rounded-full animate-bounce"
                          style={{ animationDelay: "0.1s" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-current rounded-full animate-bounce"
                          style={{ animationDelay: "0.2s" }}
                        ></div> */}
                      </div>
                      <span className="typing-dots "></span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={endRef} />
          </div>

          {/* Input Form */}
          <form className="w-full p-4" onSubmit={handleSubmit}>
            <div className="relative flex w-full items-center">
              <input
                type="text"
                placeholder="Type your message..."
                className="flex-grow rounded-l-md border border-gray-200 px-3 py-2 pr-10 focus:outline-none"
                value={input}
                readOnly={bot_type === "readonly"}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
                onKeyDown={handleKeyDown}
              />
              <div className="absolute inset-y-0 right-0 flex h-[100%] items-center">
                <button
                  type="submit"
                  className="flex h-[100%] w-9 items-center justify-center rounded-r-md text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Send"
                  disabled={!input.trim() || isLoading}
                  style={{ background: agentMessageColor }}
                >
                  {isLoading ? (
                    <svg
                      className="h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <AiOutlineSend className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Widget Toggle Button - Always visible */}
      <div
        className={`op_chatbot_icon absolute bottom-[-83px] ${
          widgetPosition === "bottom-left" ? "left-4" : "right-4"
        } ${
          isSticky ? "bottom-[-83px]" : "bottom-[96px]"
        } flex h-12 w-12 cursor-pointer items-center justify-center rounded-full transition-all duration-300 ease-in-out hover:scale-105 `}
        style={{ background: widgetIconColor }}
        onClick={handleWidgetIconClick}
      >
        {widgetIcon ? (
          <img
            src={widgetIcon}
            alt="Bot Avatar"
            className="h-10 w-10 rounded-full object-cover"
            style={{ background: widgetIconColor }}
          />
        ) : (
          <BsChatDots className="h-6 w-6 text-white" />
        )}
      </div>
    </div>
  );
};

// ... your existing PropTypes
ChatbotWidget.propTypes = {
  widgetPosition: PropTypes.oneOf(["bottom-left", "bottom-right"]),
  headerColor: PropTypes.string,
  agentMessageColor: PropTypes.string,
  agentTextColor: PropTypes.string,
  widgetIconColor: PropTypes.string,
  widgetIcon: PropTypes.string,
  input: PropTypes.string,
  setInput: PropTypes.func,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  messagesEndRef: PropTypes.object,
  bot_type: PropTypes.oneOf(["widget", "readonly", "admin_widget"]),
  liked: PropTypes.bool,
  setLiked: PropTypes.func,
  disliked: PropTypes.bool,
  setDisliked: PropTypes.func,
  copiedMsg: PropTypes.bool,
  setCopiedMsg: PropTypes.func,
  // New PropTypes for dynamic functionality
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["user", "agent"]).isRequired,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sources: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  onSendMessage: PropTypes.func,
  onStartTyping: PropTypes.func,
  onFinishTyping: PropTypes.func,
  sessionId: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSticky: PropTypes.bool,
};

export default ChatbotWidget;
